import { intl } from "LocaleWrapper";
import { Box } from "@mui/material";
import { PropertyItem } from "./ShipBayPlan";
import Bay from "./Bay";
import { getRowNumber, AttributeType } from "./utils";

interface TierProps {
	index: number;
	tiers: number;
	rows: number;
	bays: number;
	properties: PropertyItem[];
	onChange: (attribute: AttributeType, tier: number, row: number, bay: number) => void;
	onDelete: (tier: number, row: number, bay: number) => void;
	selectedAttribute?: AttributeType;
	showEvenBay: boolean;
	onChangeAllowed: boolean;
}

const Tier = ({
	index,
	tiers, rows,
	bays,
	properties,
	onChange,
	onDelete,
	selectedAttribute,
	showEvenBay,
	onChangeAllowed,
}: TierProps) => {

	const tierNumber = (tiers * 2) - (index * 2);

	const numberWithPrefix = (n: number) => (n > 9 ? n : `0${n}`);
	const getBayOddNumber = (index: number) => ((bays * 2) - (index * 2)) - 1;
	const getBayEvenNumber = (index: number) => ((bays * 2) - (index * 2)) - 2;

	return (
		<Box mb={4}>
			<Box sx={{ display: "flex" }}>
				<Box sx={{ flex: "0 0 80px", padding: "5px 0", width: "80px" }} className="rows">
					{Array.from(Array(rows).keys()).map((rowIndex) => {
						const rowNumber = getRowNumber(rows, rowIndex);
						return (
							<Box
								key={rowIndex}
								pr={2}
								sx={{
									alignItems: "center",
									display: "flex",
									fontSize: "11px",
									fontWeight: 600,
									justifyContent: "end",
									height: "30px",
									textTransform: "uppercase",
								}}>
								{`${intl.formatMessage({ id: "BAYPLAN_ROW" })} ${numberWithPrefix(rowNumber)}`}
							</Box>
						);
					})}
				</Box>

				<Box
					sx={{
						backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.action.selected : "#FFF",
						padding: "5px 0",
						width: "auto",
					}}>
					{Array.from(Array(rows).keys()).map((rowIndex) => (
						<Box
							key={rowIndex}
							pl={1}
							sx={{
								alignItems: "center",
								display: "flex",
								height: "30px",
							}}>
							{Array.from(Array(bays).keys()).map((bayIndex) => {

								const rowNumber = getRowNumber(rows, rowIndex);
								const bayNumber = getBayOddNumber(bayIndex);
								const item = properties.find(it => (it.tier === tierNumber) && (it.row === rowNumber) && (it.bay === bayNumber));
								const attribute = item?.attribute || undefined;

								return (
									<Bay
										attribute={attribute}
										onChange={newAttribute => onChange(newAttribute, tierNumber, rowNumber, bayNumber)}
										onDelete={() => onDelete(tierNumber, rowNumber, bayNumber)}
										selectedAttribute={selectedAttribute}
										onChangeAllowed={onChangeAllowed}
										key={bayIndex} />
								);
							})}
						</Box>
					))}
				</Box>

				<Box
					pl={1}
					sx={{
						alignItems: "center",
						backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.action.selected : "#FFF",
						display: "flex",
						flex: "0 0 100px",
						fontSize: "18px",
						fontWeight: 500,
						position: "relative",
						textTransform: "uppercase",
						width: "100px",

						"&:before, &:after": {
							content: "\"\"",
							borderRight: (theme) => `50px solid ${theme.palette.mode === "dark" ? theme.palette.background.paper : "#F0F0F0"}`,
							borderTop: "50px solid transparent",
							borderBottom: "50px solid transparent",
							position: "absolute",
							right: "-8px",
						},

						"&:before": {
							top: "-33px",
							transform: "rotate(135deg)",
						},

						"&:after": {
							bottom: "-33px",
							transform: "rotate(226deg)",
						},
					}}>
					{`${intl.formatMessage({ id: "BAYPLAN_TIER" })} ${numberWithPrefix(tierNumber)}`}
				</Box>
			</Box>

			<Box>
				<Box sx={{ display: "flex", paddingLeft: "80px" }} mt={1}>
					{Array.from(Array(bays).keys()).map((bayIndex) => (
						<Box
							key={bayIndex}
							ml={1}
							sx={{
								alignItems: "center",
								display: "flex",
								color: (theme) => theme.palette.error.main,
								flex: "0 0 40px",
								fontSize: "11px",
								fontWeight: 600,
								justifyContent: "center",
								width: "40px",
							}}>
							{numberWithPrefix(getBayOddNumber(bayIndex))}
						</Box>
					))}
				</Box>

				{showEvenBay && (
					<Box sx={{ display: "flex", paddingLeft: "104px" }} mt={1}>
						{Array.from(Array(bays - 1).keys()).map((bayIndex) => (
							<Box
								key={bayIndex}
								ml={1}
								sx={{
									alignItems: "center",
									display: "flex",
									color: (theme) => theme.palette.warning.main,
									flex: "0 0 40px",
									fontSize: "11px",
									fontWeight: 600,
									justifyContent: "center",
									width: "40px",
								}}>
								{numberWithPrefix(getBayEvenNumber(bayIndex))}
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default Tier;
